import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Button, Box, Checkbox, FormControlLabel, Tabs, Tab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import JoditEditor from 'jodit-react';

interface Phase {
  name: string;
  items: string[];
}

interface PreencherItemFasesEditorProps {
  title: string;
  phases: Phase[];
  considerarOrdem?: boolean;
  comment?: string;
  onSave: (title: string, phases: Phase[], considerarOrdem: boolean, comment:string) => void;
  onCancel: () => void;
}

const PreencherItemFasesEditor: React.FC<PreencherItemFasesEditorProps> = ({
  title: initialTitle,
  phases: initialPhases,
  considerarOrdem: initialConsiderarOrdem = false,
  comment: initialComment = '',
  onSave,
  onCancel,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [phases, setPhases] = useState<Phase[]>(initialPhases);
  const [considerarOrdem, setConsiderarOrdem] = useState(initialConsiderarOrdem);
  const [comment, setComment] = useState(initialComment);
  const [currentTab, setCurrentTab] = useState(0);
  const [focusPhaseIndex, setFocusPhaseIndex] = useState<number | null>(null);
  const [focusItemIndex, setFocusItemIndex] = useState<{ phaseIndex: number, itemIndex: number } | null>(null);
  const phaseRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const itemRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const commentEditorRef = useRef<any>(null);

  useEffect(() => {
    if (focusPhaseIndex !== null) {
      const ref = phaseRefs.current.get(focusPhaseIndex);
      if (ref) {
        ref.focus();
        setFocusPhaseIndex(null);
      }
    }
  }, [focusPhaseIndex]);

  useEffect(() => {
    if (focusItemIndex !== null) {
      const key = `${focusItemIndex.phaseIndex}-${focusItemIndex.itemIndex}`;
      const ref = itemRefs.current.get(key);
      if (ref) {
        ref.focus();
        setFocusItemIndex(null);
      }
    }
  }, [focusItemIndex]);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newTab: number) => {
    if (currentTab === 1 && commentEditorRef.current) {
      setComment(commentEditorRef.current.value);
    }
    setCurrentTab(newTab);
  };

  const handleAddPhase = () => {
    const newPhase: Phase = { name: '', items: [''] };
    setPhases([...phases, newPhase]);
    setFocusPhaseIndex(phases.length);
  };

  const handleEditPhaseName = (index: number, name: string) => {
    const updatedPhases = [...phases];
    updatedPhases[index].name = name;
    setPhases(updatedPhases);
  };

  const handleAddItem = (phaseIndex: number) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].items.push('');
    setPhases(updatedPhases);
    setFocusItemIndex({ phaseIndex, itemIndex: updatedPhases[phaseIndex].items.length - 1 });
  };

  const handleEditItem = (phaseIndex: number, itemIndex: number, newItem: string) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].items[itemIndex] = newItem;
    setPhases(updatedPhases);
  };

  const handleRemovePhase = (index: number) => {
    const updatedPhases = phases.filter((_, i) => i !== index);
    setPhases(updatedPhases);
  };

  const handleRemoveItem = (phaseIndex: number, itemIndex: number) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].items.splice(itemIndex, 1);
    setPhases(updatedPhases);
  };

  const handleMovePhase = (index: number, direction: 'up' | 'down') => {
    const newPhases = [...phases];
    const [movedPhase] = newPhases.splice(index, 1);
    newPhases.splice(direction === 'up' ? index - 1 : index + 1, 0, movedPhase);
    setPhases(newPhases);
  };

  const handleSaveAll = () => {
    const currentComment = commentEditorRef.current?.value || comment;
    onSave(title, phases, considerarOrdem, currentComment);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>, phaseIndex: number, itemIndex?: number) => {
    const newValue = e.target.innerText.trim();
    if (itemIndex !== undefined) {
      handleEditItem(phaseIndex, itemIndex, newValue);
    } else {
      handleEditPhaseName(phaseIndex, newValue);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>, phaseIndex: number, itemIndex?: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (itemIndex !== undefined) {
        handleAddItem(phaseIndex);
      } else {
        handleAddPhase();
      }
    }
  };

  const config = {
    readonly: false,
    toolbar: true,
    height: 400,
    autofocus: true,
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'superscript', 'subscript', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'image', 'video', 'table', 'link', '|',
      'align', 'undo', 'redo', '|',
      'hr', 'eraser', 'copyformat', 'symbols', '|',
      'fullsize', 'selectall', 'print', 'about', '|'
    ]
  };

  return (
    <div className="preencher-item-fases-editor">
      <h2>Editor de Preencher Item Fases</h2>
      <Tabs value={currentTab} onChange={handleTabChange}>
        <Tab label="Fases e Itens" />
        <Tab label="Comentário" />
      </Tabs>
      {currentTab === 0 && (
      <Box>
        <label>Título</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ width: '100%', padding: '8px', marginBottom: '16px' }}
        />
      
      <FormControlLabel
        control={<Checkbox checked={considerarOrdem} onChange={(e) => setConsiderarOrdem(e.target.checked)} />}
        label="Considerar Ordem dos Itens"
      />
      <Button variant="contained" color="primary" onClick={handleAddPhase}>
        Adicionar Fase
      </Button>

      <h3>Fases</h3>
      <ul>
        {phases.map((phase, phaseIndex) => (
          <li key={phaseIndex}>
            <Box display="flex" alignItems="center">
              <div
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => handleBlur(e, phaseIndex)}
                onKeyPress={(e) => handleKeyPress(e, phaseIndex)}
                ref={(el) => phaseRefs.current.set(phaseIndex, el)}
                style={{ borderBottom: '1px dashed #ccc', cursor: 'text', padding: '5px', flexGrow: 1 }}
              >
                {phase.name}
              </div>
              <IconButton onClick={() => handleRemovePhase(phaseIndex)}><DeleteIcon /></IconButton>
              <IconButton
                onClick={() => handleMovePhase(phaseIndex, 'up')}
                disabled={phaseIndex === 0}
              >
                <ArrowUpwardIcon />
              </IconButton>
              <IconButton
                onClick={() => handleMovePhase(phaseIndex, 'down')}
                disabled={phaseIndex === phases.length - 1}
              >
                <ArrowDownwardIcon />
              </IconButton>
            </Box>
            <ul>
              {phase.items.map((item, itemIndex) => (
                <li key={itemIndex}>
                  <Box display="flex" alignItems="center">
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => handleBlur(e, phaseIndex, itemIndex)}
                      onKeyPress={(e) => handleKeyPress(e, phaseIndex, itemIndex)}
                      ref={(el) => itemRefs.current.set(`${phaseIndex}-${itemIndex}`, el)}
                      style={{ borderBottom: '1px dashed #ccc', cursor: 'text', padding: '5px', flexGrow: 1 }}
                    >
                      {item}
                    </div>
                    <IconButton onClick={() => handleRemoveItem(phaseIndex, itemIndex)}><DeleteIcon /></IconButton>
                  </Box>
                </li>
              ))}
              <li>
                <IconButton onClick={() => handleAddItem(phaseIndex)}><AddIcon /></IconButton>
              </li>
            </ul>
          </li>
        ))}
      </ul>
      </Box>
      )}

      {currentTab === 1 && (
        <Box>
          <JoditEditor ref={commentEditorRef} value={comment} config={config} />
        </Box>
      )}

      <Button variant="contained" color="primary" onClick={handleSaveAll}>
        Salvar Tudo
      </Button>
      <Button variant="contained" color="secondary" onClick={onCancel}>
        Cancelar
      </Button>
    </div>
  );
};

export default PreencherItemFasesEditor;
